import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../platform/auth/AuthProvider';
import { useTheme } from '../platform/theme/ThemeProvider';
import { isStandalone } from '../platform/detector';
import { useTranslation } from '../hooks/useTranslation';
import { LoginModal } from './LoginModal';
import { Settings, ChevronDown, LogOut } from 'lucide-react';
import { Button } from './ui';
import { cn } from '../lib/utils';
import logoIcon from '../img/logo_icon.png';

/**
 * Header component for standalone web mode
 * Shows logo, navigation links (for guests), and account selector
 */
export function StandaloneHeader({ setAppLocation, currentPage }) {
  const { t } = useTranslation();
  const { user, isGuest, logout } = useAuth();
  useTheme(); // Keep theme context for potential future use
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render in Telegram mode
  if (!isStandalone) return null;

  const handleLogoClick = () => {
    setAppLocation('/accounts');
  };

  const handleAccountsClick = () => {
    setAppLocation('/accounts');
  };

  const handleSettingsClick = () => {
    setAppLocation('/settings');
  };

  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  const handleLogout = () => {
    setShowUserDropdown(false);
    logout();
  };

  // Determine active nav item
  const isAccountsActive = currentPage?.type === 'accounts' || currentPage?.type === 'account-balance';

  // Get display name for user
  const getUserDisplayName = () => {
    if (!user) return '';
    if (user.firstName) {
      return user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName;
    }
    return user.username || 'User';
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 h-14 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-2xl mx-auto h-full px-4 flex items-center justify-between">
          {/* Logo - clickable to home */}
          <button
            className="flex items-center gap-2 hover:opacity-90 transition-opacity"
            onClick={handleLogoClick}
          >
            <img src={logoIcon} alt="BuyTelegramAds" className="h-8 w-8 object-contain" />
            <span className="text-xl font-bold text-foreground hidden sm:block">BuyTelegramAds</span>
          </button>

          {/* Navigation links - only show for guests */}
          {isGuest && (
            <nav className="hidden sm:flex items-center gap-6">
              <button
                className={cn(
                  "text-sm font-medium transition-colors",
                  isAccountsActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                )}
                onClick={handleAccountsClick}
              >
                {t('head_accounts') || 'Accounts'}
              </button>
            </nav>
          )}

          {/* Right side - Account selector + Settings */}
          <div className="flex items-center gap-2">
            {isGuest ? (
              <Button size="sm" onClick={handleLoginClick}>
                {t('head_login') || 'Login'}
              </Button>
            ) : (
              <>
                {/* User name with dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-secondary transition-colors"
                    onClick={() => setShowUserDropdown(!showUserDropdown)}
                  >
                    <span className="max-w-[120px] truncate hidden sm:inline">{getUserDisplayName()}</span>
                    <ChevronDown size={14} className={cn("transition-transform", showUserDropdown && "rotate-180")} />
                  </button>
                  {showUserDropdown && (
                    <div className="absolute right-0 top-full mt-1 min-w-[160px] rounded-lg border bg-card shadow-lg py-1 z-50">
                      <button
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                        onClick={handleLogout}
                      >
                        <LogOut size={16} />
                        <span>{t('head_logout') || 'Logout'}</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Settings button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleSettingsClick}
                  aria-label={t('head_settings') || 'Settings'}
                >
                  <Settings size={20} />
                </Button>
              </>
            )}
          </div>
        </div>
      </header>
      {/* Spacer for fixed header */}
      <div className="h-14" />
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </>
  );
}

export default StandaloneHeader;
